import type { ReactNode } from 'react';

/**
 * The strip under the top bar on a screen that has several sections.
 *
 * Ten rail destinations replaced twenty-three, and the thirteen that lost a
 * rail button did not lose a screen: each is a section here. The strip is
 * always visible on such a screen, so somebody who followed `/policies` from
 * a link sees where Policies now lives without being told.
 *
 * Buttons rather than links or tabs, matching the rail: navigation within
 * the studio is a state change the URL follows, and a screen reader that
 * announces "Policies, button, current" for a rail item should announce the
 * same for a section — two vocabularies for one idea is one too many.
 */
export interface SubNavItem {
  id: string;
  label: string;
  count?: number;
}

export function SubNav({
  title,
  items,
  current,
  onChange,
  children,
}: {
  title: string;
  items: readonly SubNavItem[];
  current: string;
  onChange(id: string): void;
  /** Controls that belong to the whole screen — a New button, a window picker. */
  children?: ReactNode;
}) {
  return (
    <div className="subnav">
      <span className="subnav-title">{title}</span>
      <div className="subnav-tabs" aria-label={`${title} sections`}>
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className="subnav-tab"
            aria-current={current === item.id ? 'true' : undefined}
            aria-label={item.count ? `${item.label} (${item.count})` : item.label}
            onClick={() => onChange(item.id)}
          >
            {item.label}
            {item.count ? <span className="rail-count">{item.count}</span> : null}
          </button>
        ))}
      </div>
      <span className="spacer" />
      {children}
    </div>
  );
}
